import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { InvitableMember } from '../types/api';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';
import { Modal, Button } from './ui';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

interface InviteMemberModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = (useTranslation as any)('organisations');
  const [activeTab, setActiveTab] = useState<'org-members' | 'external'>('org-members');
  const [orgMembers, setOrgMembers] = useState<InvitableMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [externalEmail, setExternalEmail] = useState<string>('');
  const [role, setRole] = useState<string>('developer');
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Charger les membres invitables quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      loadInvitableMembers();
    }
  }, [isOpen, projectId]);

  const loadInvitableMembers = async () => {
    setLoadingMembers(true);
    try {
      const members = await apiService.getInvitableMembers(projectId);
      setOrgMembers(members);

      if (members.length === 0) {
        // Si aucun membre disponible, basculer sur l'onglet email externe
        setActiveTab('external');
      }
    } catch (error: unknown) {
      Logger.error('Erreur chargement membres:', error);
      toast.error(ErrorMessages.GENERIC_ERROR);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let email = '';

      if (activeTab === 'org-members') {
        // Trouver l'email du membre sélectionné
        const selectedMember = orgMembers.find(m => m.utilisateur_id === selectedMemberId);
        if (!selectedMember) {
          toast.error(ErrorMessages.VALIDATION_ERROR);
          setLoading(false);
          return;
        }
        email = selectedMember.email;
      } else {
        // Utiliser l'email externe saisi
        if (!externalEmail.trim()) {
          toast.error(ErrorMessages.VALIDATION_ERROR);
          setLoading(false);
          return;
        }
        email = externalEmail.trim();
      }

      // Envoyer l'invitation (même endpoint pour les deux cas)
      const result = await apiService.addProjectMember(projectId, {
        email_utilisateur_invite: email,
        role_projet: role as 'admin' | 'developer' | 'user'
      });

      // Message de succès selon le type
      if (result.type === 'member_added') {
        toast.success(SuccessMessages.ORG_MEMBER_ADDED);
      } else if (result.type === 'invitation_sent') {
        toast.success(SuccessMessages.INVITATION_SENT);
      }

      onSuccess();
      handleClose();
    } catch (error: unknown) {
      Logger.error('Erreur invitation:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedMemberId('');
    setExternalEmail('');
    setRole('developer');
    setActiveTab('org-members');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('members.inviteProjectModal.title') as string} size="md">
      <div className="p-6">
        {/* Onglets */}
        <div className="flex border-b border-border-1 mb-4">
          <button
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              activeTab === 'org-members'
                ? 'border-b-2 border-accent-500 text-accent-400'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
            onClick={() => setActiveTab('org-members')}
          >
            {t('members.inviteProjectModal.orgMembersTab')}
            {orgMembers.length > 0 && (
              <span className="ml-2 bg-blue-500/12 text-blue-400 text-xs px-2 py-0.5 rounded-full">
                {orgMembers.length}
              </span>
            )}
          </button>
          <button
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              activeTab === 'external'
                ? 'border-b-2 border-accent-500 text-accent-400'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
            onClick={() => setActiveTab('external')}
          >
            {t('members.inviteProjectModal.externalTab')}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Contenu onglet Membres Organisation */}
          {activeTab === 'org-members' && (
            <div className="mb-4">
              {loadingMembers ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500 mx-auto"></div>
                  <p className="text-text-tertiary mt-2">{t('members.inviteProjectModal.loading')}</p>
                </div>
              ) : orgMembers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-text-tertiary">
                    {t('members.inviteProjectModal.noMembers')}
                  </p>
                  <p className="text-sm text-text-quaternary mt-1">
                    {t('members.inviteProjectModal.useEmailTab')}
                  </p>
                </div>
              ) : (
                <>
                  <label htmlFor="member-select" className="block text-sm font-medium text-text-secondary mb-2">
                    {t('members.inviteProjectModal.selectMember')}
                  </label>
                  <select
                    id="member-select"
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value)}
                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                    required
                    aria-required="true"
                  >
                    <option value="">{t('members.inviteProjectModal.chooseMember')}</option>
                    {orgMembers.map((member) => (
                      <option key={member.utilisateur_id} value={member.utilisateur_id}>
                        {member.prenom} {member.nom} ({member.email})
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}

          {/* Contenu onglet Email Externe */}
          {activeTab === 'external' && (
            <div className="mb-4">
              <label htmlFor="external-email" className="block text-sm font-medium text-text-secondary mb-2">
                {t('members.inviteProjectModal.emailLabel')}
              </label>
              <input
                id="external-email"
                type="email"
                value={externalEmail}
                onChange={(e) => setExternalEmail(e.target.value)}
                placeholder="externe@example.com"
                className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                required
                aria-required="true"
              />
              <p className="text-xs text-text-quaternary mt-1">
                {t('members.inviteProjectModal.emailHelp')}
              </p>
            </div>
          )}

          {/* Sélecteur de rôle (commun aux deux onglets) */}
          <div className="mb-6">
            <label htmlFor="role-select" className="block text-sm font-medium text-text-secondary mb-2">
              {t('members.inviteProjectModal.roleLabel')}
            </label>
            <select
              id="role-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              <option value="admin">{t('members.inviteProjectModal.roleAdmin')}</option>
              <option value="developer">{t('members.inviteProjectModal.roleDeveloper')}</option>
              <option value="user">{t('members.inviteProjectModal.roleUser')}</option>
            </select>
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border-1">
            <Button
              variant="secondary"
              onClick={handleClose}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={loading || (activeTab === 'org-members' && orgMembers.length === 0)}
            >
              Inviter
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
